import {
  GetUsersResponse,
  OrganizationUnit,
  OrganizationUnitType,
  UserStatus,
  UserType,
  VenueBranch,
} from '@inplay/models';
import { TableHeader } from '../../../component/table/table.type';
import { FilterKey, OrgUnit } from '../../../utils/helper.type';
import {
  StatusOption,
  VenuesViewAddOptions,
  VenuesViewSearchParams,
  VenuesViewTableHeaderKey,
  ViewByOption,
} from './venuesView.type';
import { VenuesViewTableColumns } from '../../../strategies/orgUnitPresentation/orgUnitPresentationTypes';
import { getUsersByOrganizationUnit } from '../userManagementHelper';
import moment from 'moment';
import { getStatusDotColor } from '../../../utils/helper';
import { useSearchParams } from 'react-router-dom';
import { useOrganizationContext } from '../../../service/context/organizationContext';
import { OrganizationContextType } from '../../../service/context/organizationContext.types';
import BackIcon from '../../../assets/back.svg';
import { DropdownOption } from '../../../component/dropDownMenu/dropDownMenu.type';
import { OrganisationToggleEnum } from '../userManagement.type';
import { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import leftArrow from '../../../assets/left-arrow.png';
import { Option } from '../../../component/dropDownInput/dropDownInput.type';
import { UserManagementFilters } from '../../../component/userManagementFilter/type';

export const getOrganizationUnitsByTypeAndParent = (
  orgUnits: OrganizationUnit[],
  type: OrganizationUnitType,
  parentOrganizationUnitId?: string,
): OrganizationUnit[] => {
  return orgUnits.filter(
    (orgUnit: OrganizationUnit) =>
      // Check if the orgUnit is of the specified type
      orgUnit.type === type &&
      // if no parentOrganizationUnitId is provided, return all orgUnits of the specified type
      (!parentOrganizationUnitId ||
        // else check if the orgUnit has the specified parent organization unit ID
        (orgUnit as VenueBranch).organizationId === parentOrganizationUnitId ||
        (orgUnit as VenueBranch).venueId === parentOrganizationUnitId ||
        (orgUnit as VenueBranch).subOrganizationIds?.includes(
          parentOrganizationUnitId,
        )),
  );
};

export const applyFiltersToOrgUnits = (
  allOrgUnits: OrgUnit[],
  query?: string,
): OrgUnit[] => {
  let filteredOrgUnits: OrgUnit[] = allOrgUnits;

  if (query) {
    filteredOrgUnits = filteredOrgUnits.filter((orgUnit: OrgUnit) =>
      orgUnit.name.toLowerCase().includes(query.toLowerCase()),
    );
  }
  return filteredOrgUnits;
};

/**
 * Filters organization units (orgUnits) based on various user-related filters.
 *
 * @param orgUnits - Array of all organization units (e.g., Organizations, SubOrganizations, VenueBranches).
 * @param filters - UserManagementFilters including user status, view options, date range, etc.
 * @param usersList - List of all users for the current context, used for filtering by user status or counts.
 * @returns A filtered list of OrgUnits based on the applied filters.
 */
export const filterOrgUnits = (
  orgUnits: OrgUnit[],
  filters: UserManagementFilters,
  usersList: GetUsersResponse[],
): OrgUnit[] => {
  const isFilterEmpty = (value: unknown, key?: string): boolean => {
    if (Array.isArray(value)) return value.length === 0;

    if (value && typeof value === 'object') {
      if (key === FilterKey.DATE_RANGE) {
        const dateRange = value as { from?: string | Date; to?: string | Date };
        return !dateRange.from && !dateRange.to;
      }
      return Object.entries(value).every(([k, v]) => isFilterEmpty(v, k));
    }

    return value === undefined || value === null || value === '';
  };

  const noFiltersApplied: boolean =
    !filters ||
    Object.entries(filters).every(([key, val]) => isFilterEmpty(val, key));
  if (noFiltersApplied) return orgUnits;

  const { dateRange, selectedViewBy = [] } = filters;

  const status: string[] = filters?.userStatus?.map((status: string) =>
    status.toLowerCase(),
  );

  return orgUnits.filter((orgUnit: OrgUnit) => {
    const createdOnRaw: Date | undefined = orgUnit.createdOn;

    // Date range filter
    let inDateRange = true;
    if (dateRange?.from && dateRange?.to) {
      if (!createdOnRaw) {
        inDateRange = false;
      } else {
        const fromDate = new Date(dateRange.from);
        const toDate = new Date(dateRange.to);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        const createdOn = new Date(createdOnRaw);
        inDateRange = createdOn >= fromDate && createdOn <= toDate;
      }
    }

    // Status filter
    let statusMatch = true;
    if (status?.length) {
      if (status.includes(StatusOption.ACTIVE)) {
        statusMatch = true;
      } else if (
        status.includes(StatusOption.INACTIVE) &&
        !status.includes(FilterKey.Active)
      ) {
        statusMatch = false;
      }
    }

    // View by filter logic (multi-select)
    let selectedViewMatch = true;

    if (selectedViewBy.length > 0) {
      // Cache user counts once per orgUnit
      const activeUserCount = getUsersByOrganizationUnit(
        usersList,
        orgUnit._id,
        UserStatus.ACTIVE,
      ).length;

      const inactiveUserCount = getUsersByOrganizationUnit(
        usersList,
        orgUnit._id,
        UserStatus.INACTIVE,
      ).length;

      selectedViewMatch = selectedViewBy.some((selectedView: ViewByOption) => {
        const normalizedView: string = selectedView.toLowerCase();
        const orgType: string = orgUnit.type?.toLowerCase();

        switch (normalizedView) {
          case ViewByOption.SUB_ORGANIZATION_WITH_INACTIVE_USER:
            return inactiveUserCount > 0;

          case ViewByOption.SUB_ORGANIZATION_WITH_NO_ACTIVE_USER:
            return activeUserCount === 0;

          case ViewByOption.SUB_ORGANIZATION_WITH_NO_USER:
            return activeUserCount === 0 && inactiveUserCount === 0;

          default:
            return orgType === normalizedView;
        }
      });
    }

    return inDateRange && statusMatch && selectedViewMatch;
  });
};

export const addCellContentToTableHeader = (
  tableHeaderNames: VenuesViewTableColumns[],
  usersList: GetUsersResponse[],
  orgUnits: OrganizationUnit[],
): TableHeader<OrgUnit>[] => {
  return tableHeaderNames.map((header: VenuesViewTableColumns) => {
    let cellContent: TableHeader<OrgUnit>['generateCellContent'];

    switch (header.header) {
      case VenuesViewTableHeaderKey.VENUE_BRANCH_NAME:
      case VenuesViewTableHeaderKey.VENUE_NAME:
      case VenuesViewTableHeaderKey.SUB_ORG_NAME:
      case VenuesViewTableHeaderKey.ORG_NAME:
        cellContent = (rowOrgUnit: OrgUnit) => rowOrgUnit.name;
        break;

      case VenuesViewTableHeaderKey.STATUS:
        cellContent = () => (
          <>
            <span
              className={`w-2 h-2 mr-2 rounded-full inline-block ${getStatusDotColor(
                UserStatus.ACTIVE,
              )}`}
            />
            <span className="capitalize">{UserStatus.ACTIVE}</span>
          </>
        );
        break;

      case VenuesViewTableHeaderKey.ACTIVE_USERS: {
        cellContent = (rowOrgUnit: OrgUnit) => {
          const activeUsers = getUsersByOrganizationUnit(
            usersList,
            rowOrgUnit._id,
            UserStatus.ACTIVE,
          );
          return activeUsers.length;
        };
        break;
      }

      case VenuesViewTableHeaderKey.INACTIVE_USERS: {
        cellContent = (rowOrgUnit: OrgUnit) => {
          const inactiveUsers = getUsersByOrganizationUnit(
            usersList,
            rowOrgUnit._id,
            UserStatus.INACTIVE,
          );
          return inactiveUsers.length;
        };
        break;
      }

      case VenuesViewTableHeaderKey.SUB_ORGS: {
        cellContent = (rowOrgUnit: OrgUnit) => {
          const subOrgs = getOrganizationUnitsByTypeAndParent(
            orgUnits,
            OrganizationUnitType.SUB_ORGANIZATION,
            rowOrgUnit._id,
          );
          return subOrgs.length;
        };
        break;
      }

      case VenuesViewTableHeaderKey.VENUES: {
        cellContent = (rowOrgUnit: OrgUnit) => {
          const venues = getOrganizationUnitsByTypeAndParent(
            orgUnits,
            OrganizationUnitType.VENUE,
            rowOrgUnit._id,
          );

          return venues.length;
        };
        break;
      }

      case VenuesViewTableHeaderKey.VENUE_BRANCHES: {
        cellContent = (rowOrgUnit: OrgUnit) => {
          const branches = getOrganizationUnitsByTypeAndParent(
            orgUnits,
            OrganizationUnitType.VENUE_BRANCH,
            rowOrgUnit._id,
          );
          return branches.length;
        };
        break;
      }

      case VenuesViewTableHeaderKey.ADDED_BY:
        cellContent = (rowOrgUnit: OrgUnit) =>
          rowOrgUnit.createdBy
            ? `${rowOrgUnit.createdBy.firstName} ${
                rowOrgUnit.createdBy.lastName
              }, ${moment(rowOrgUnit.createdOn).format('DD/MM/YYYY')}`
            : '-';
        break;

      default:
        cellContent = () =>
          'Table header not mapped to cell content. Check addCellContentToTableHeader';
        break;
    }

    const tableHeader: TableHeader<OrgUnit> = {
      ...header,
      generateCellContent: cellContent,
    };
    return tableHeader;
  });
};

export const NestedHeader = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const context: OrganizationContextType = useOrganizationContext();

  const selectedOrgUnitIds: string[] | null =
    searchParams
      .get(VenuesViewSearchParams.SELECTED_ORG_UNIT_IDS)
      ?.split(',') || null;

  const orgUnit: OrgUnit | undefined =
    context.organizationSummary && selectedOrgUnitIds
      ? context.organizationSummary.find(
          (orgUnit: OrgUnit) => orgUnit._id === selectedOrgUnitIds[0],
        )
      : undefined;

  const orgUnitParent: OrgUnit | undefined =
    context.organizationSummary &&
    selectedOrgUnitIds &&
    selectedOrgUnitIds.length > 1
      ? context.organizationSummary.find(
          (orgUnit: OrgUnit) => orgUnit._id === selectedOrgUnitIds[1],
        )
      : undefined;

  const showUsers =
    searchParams.get(VenuesViewSearchParams.SHOW_USERS) === 'true';

  const handleClickBackButton = () => {
    setSearchParams((prevParams) => {
      const nestedLevel = prevParams.get(VenuesViewSearchParams.NESTED_LEVEL);
      const newNestedLevel = nestedLevel ? parseInt(nestedLevel) - 1 : 0;

      const prevOrgUnitIds: string[] =
        prevParams
          .get(VenuesViewSearchParams.SELECTED_ORG_UNIT_IDS)
          ?.split(',') || [];

      const newOrgUnitIds: string[] = prevOrgUnitIds.slice(1);
      return {
        ...prevParams,
        [VenuesViewSearchParams.NESTED_LEVEL]: newNestedLevel.toString(),
        [VenuesViewSearchParams.SELECTED_ORG_UNIT_IDS]: newOrgUnitIds.join(','),
      };
    });
  };

  return (
    <div className="flex items-center">
      <button onClick={handleClickBackButton} className="flex items-center">
        <img src={BackIcon} alt="Back Arrow" className="w-5 h-5 mx-2" />
        {showUsers &&
          orgUnitParent?.type === OrganizationUnitType.VENUE &&
          `${orgUnitParent.name}`}{' '}
        {orgUnit?.name}
      </button>
    </div>
  );
};

export const getAddMenuOptions = (
  view: OrganisationToggleEnum,
  currentOrgUnitType: OrganizationUnitType,
  isNestedView: boolean,
  loggedInUserType: UserType,
): DropdownOption<VenuesViewAddOptions>[] => {
  if (isNestedView) {
    if (currentOrgUnitType === OrganizationUnitType.SUB_ORGANIZATION) {
      return [
        {
          label: 'userManagement.addSubOrganisation',
          optionKey: VenuesViewAddOptions.ADD_SUB_ORGANISATION,
        },
      ];
    } else if (currentOrgUnitType === OrganizationUnitType.VENUE) {
      return [
        {
          label: 'userManagement.addVenue',
          optionKey: VenuesViewAddOptions.ADD_VENUE,
        },
      ];
    } else if (
      currentOrgUnitType === OrganizationUnitType.VENUE_BRANCH &&
      loggedInUserType === UserType.ORGANIZATION_ADMIN
    ) {
      return [
        {
          label: 'userManagement.addVenueBranch',
          optionKey: VenuesViewAddOptions.ADD_VENUE_BRANCH,
        },
      ];
    }
  }
  if (view === OrganisationToggleEnum.ORGANISATION) {
    return [
      {
        label: 'userManagement.add',
        optionKey: VenuesViewAddOptions.ADD_USER,
      },
    ];
  } else {
    // Venues view
    const addMenu: DropdownOption<VenuesViewAddOptions>[] = [
      {
        label: 'userManagement.addVenue',
        optionKey: VenuesViewAddOptions.ADD_VENUE,
      },
    ];
    if (loggedInUserType === UserType.ORGANIZATION_ADMIN) {
      addMenu.unshift({
        label: 'userManagement.addSubOrganisation',
        optionKey: VenuesViewAddOptions.ADD_SUB_ORGANISATION,
      });
      addMenu.push({
        label: 'userManagement.addVenueBranch',
        optionKey: VenuesViewAddOptions.ADD_VENUE_BRANCH,
      });
    }
    return addMenu;
  }
};

export const modalHeader = (
  title: string,
  showCreate?: boolean,
  onBack?: () => void,
): ReactNode => {
  if (showCreate) {
    return (
      <div className="flex items-center gap-2 text-[#1A1A1A] font-semibold text-[16px] ">
        <button
          onClick={() => {
            onBack && onBack();
          }}
        >
          <img src={leftArrow} className="h-[14px]" alt="" />
        </button>
        <Trans i18nKey={'userManagement.review'} />
      </div>
    );
  }
  return <Trans i18nKey={title} />;
};

export const getViewByOptions = (
  orgUnitType: OrganizationUnitType,
): Option<ViewByOption>[] => {
  let labelPrefix = 'Units';

  switch (orgUnitType) {
    case OrganizationUnitType.ORGANIZATION:
      labelPrefix = 'Organizations';
      break;
    case OrganizationUnitType.SUB_ORGANIZATION:
      labelPrefix = 'Sub-organisations';
      break;
    case OrganizationUnitType.VENUE_BRANCH:
      labelPrefix = 'Venue branches';
      break;
    case OrganizationUnitType.VENUE:
      labelPrefix = 'Venues';
      break;
  }

  return [
    {
      label: `${labelPrefix} with no users`,
      value: ViewByOption.SUB_ORGANIZATION_WITH_NO_USER,
    },
    {
      label: `${labelPrefix} with inactive users`,
      value: ViewByOption.SUB_ORGANIZATION_WITH_INACTIVE_USER,
    },
    {
      label: `${labelPrefix} with no active users`,
      value: ViewByOption.SUB_ORGANIZATION_WITH_NO_ACTIVE_USER,
    },
  ];
};
